import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Download,
  Loader2,
  Printer,
  RefreshCw,
  Search,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
} from '../../components/ui';

interface TrabajadorRow {
  id: string;
  trabajador_codigo: string | null;
  trabajador_nombre: string;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  cantidad_produccion: number | null;
  aprobado_planilla: boolean;
  estado_jornada?: string | null;
  boleta_id: string;
}

interface BoletaRow {
  id: string;
  numero: string;
  fecha: string;
  semana: string | null;
  supervisor_nombre: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
}

interface ReporteLinea {
  codigo: string;
  nombre: string;
  hn: number;
  he: number;
  unidades: number;
  boletas: Set<string>;
  lotes: Set<string>;
  supervisores: Set<string>;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtHoras(v?: number | null) {
  const n = Number(v ?? 0);
  return n ? n.toFixed(2) : '';
}

function csvCell(value: unknown) {
  const s = String(value ?? '').replace(/"/g, '""');
  return `"${s}"`;
}

function joinSet(s: Set<string>) {
  return [...s].filter(Boolean).join(', ');
}

export function ReporteJornadasAprobadasPage() {
  const empresaId = useEmpresaId();
  const [empresaNombre, setEmpresaNombre] = useState('Jornadas Aprobadas');
  const [fechaDesde, setFechaDesde] = useState(addDays(today(), -7));
  const [fechaHasta, setFechaHasta] = useState(today());
  const [q, setQ] = useState('');
  const [trabajadores, setTrabajadores] = useState<TrabajadorRow[]>([]);
  const [boletas, setBoletas] = useState<BoletaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const [{ data: emp }, { data: b, error: eB }] = await Promise.all([
      supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle(),
      supabase
        .from('campo_boletas')
        .select('id, numero, fecha, semana, supervisor_nombre, finca_nombre, lote_num')
        .eq('empresa_id', empresaId)
        .gte('fecha', fechaDesde)
        .lte('fecha', fechaHasta)
        .order('fecha', { ascending: true }),
    ]);

    if (emp?.nombre) setEmpresaNombre(String(emp.nombre).toUpperCase());
    if (eB) {
      setError(eB.message);
      setBoletas([]);
      setTrabajadores([]);
      setLoading(false);
      return;
    }

    const ids = (b ?? []).map((x) => x.id);
    if (ids.length === 0) {
      setBoletas([]);
      setTrabajadores([]);
      setLoading(false);
      return;
    }

    const { data: t, error: eT } = await supabase
      .from('campo_boleta_trabajadores')
      .select('id, trabajador_codigo, trabajador_nombre, horas_ordinarias, horas_extra, cantidad_produccion, aprobado_planilla, estado_jornada, boleta_id')
      .eq('empresa_id', empresaId)
      .in('boleta_id', ids)
      .or('aprobado_planilla.eq.true,estado_jornada.eq.aprobada')
      .order('trabajador_nombre');

    if (eT) setError(eT.message);
    setBoletas((b ?? []) as BoletaRow[]);
    setTrabajadores((t ?? []) as TrabajadorRow[]);
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const lineas = useMemo<ReporteLinea[]>(() => {
    const boletaMap = new Map(boletas.map((b) => [b.id, b]));
    const map = new Map<string, ReporteLinea>();

    trabajadores.forEach((t) => {
      const codigo = t.trabajador_codigo || '';
      const key = codigo || t.trabajador_nombre;
      const b = boletaMap.get(t.boleta_id);

      if (!map.has(key)) {
        map.set(key, {
          codigo,
          nombre: t.trabajador_nombre,
          hn: 0,
          he: 0,
          unidades: 0,
          boletas: new Set(),
          lotes: new Set(),
          supervisores: new Set(),
        });
      }

      const row = map.get(key)!;
      row.hn += Number(t.horas_ordinarias ?? 0);
      row.he += Number(t.horas_extra ?? 0);
      row.unidades += Number(t.cantidad_produccion ?? 0);
      if (b?.numero) row.boletas.add(b.numero);
      if (b?.lote_num) row.lotes.add(b.lote_num);
      if (b?.supervisor_nombre) row.supervisores.add(b.supervisor_nombre);
    });

    return [...map.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [trabajadores, boletas]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return lineas;
    return lineas.filter((l) => `${l.codigo} ${l.nombre} ${joinSet(l.boletas)} ${joinSet(l.lotes)}`.toLowerCase().includes(term));
  }, [lineas, q]);

  const totales = useMemo(() => filtradas.reduce((acc, l) => ({
    hn: acc.hn + l.hn,
    he: acc.he + l.he,
    unidades: acc.unidades + l.unidades,
  }), { hn: 0, he: 0, unidades: 0 }), [filtradas]);

  function exportarCsv() {
    const headers = ['Empleado', 'Nombre', 'H/N', 'H/E', 'Unidades', 'Boletas', 'Lotes', 'Supervisores'];
    const lines = filtradas.map((l) => [
      l.codigo,
      l.nombre,
      l.hn.toFixed(2),
      l.he.toFixed(2),
      l.unidades.toFixed(2),
      joinSet(l.boletas),
      joinSet(l.lotes),
      joinSet(l.supervisores),
    ].map(csvCell).join(','));
    const blob = new Blob([[headers.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jornadas-aprobadas-${fechaDesde}-${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #jornadas-print, #jornadas-print * { visibility: visible; }
          #jornadas-print { position: absolute; inset: 0; width: 100%; background: #fff; color: #000; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Jornadas Aprobadas</h1>
            <p className={pageSubtitleCls}>Reporte operativo de horas aprobadas para enviar al ERP-MYA</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:flex xl:items-end">
            <div>
              <label className={labelCls}>Desde</label>
              <input type="date" className={inputCls} value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input type="date" className={inputCls} value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => window.print()} disabled={filtradas.length === 0}>
              <Printer size={14} />
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="no-print mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        <div className="no-print mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input className={`${inputCls} pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empleado, boleta o lote" />
              </div>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={exportarCsv} disabled={filtradas.length === 0}>
              <Download size={14} />
              Exportar
            </button>
            <div className="text-xs" style={{ color: 'var(--ink-faint)' }}>
              {boletas.length} boleta(s) / {filtradas.length} empleado(s)
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando reporte...
          </div>
        ) : (
          <div id="jornadas-print" className="mx-auto min-w-[980px] max-w-[1120px] bg-white p-8 text-black shadow-xl print:shadow-none">
            <div className="mb-5 text-center">
              <div className="font-serif text-sm font-bold tracking-wide text-red-900">{empresaNombre}</div>
              <div className="font-serif text-lg font-bold italic text-green-700">Reporte de Jornadas Aprobadas</div>
              <div className="text-sm font-semibold">
                Del: <span className="font-normal">{fmtDate(fechaDesde)}</span>
                <span className="mx-2">al</span>
                <span className="font-normal">{fmtDate(fechaHasta)}</span>
              </div>
            </div>

            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr>
                  <th className="border border-black px-1 py-1 font-normal">Empleado</th>
                  <th className="border border-black px-1 py-1 font-normal">Nombre</th>
                  <th className="border border-black px-1 py-1 font-normal">H/N</th>
                  <th className="border border-black px-1 py-1 font-normal">H/E</th>
                  <th className="border border-black px-1 py-1 font-normal">Unidades</th>
                  <th className="border border-black px-1 py-1 font-normal">Boletas</th>
                  <th className="border border-black px-1 py-1 font-normal">Lotes</th>
                  <th className="border border-black px-1 py-1 font-normal">Supervisor</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((l) => (
                  <tr key={`${l.codigo}-${l.nombre}`}>
                    <td className="border border-black px-1 py-1 font-mono">{l.codigo}</td>
                    <td className="border border-black px-1 py-1">{l.nombre}</td>
                    <td className="border border-black px-1 py-1 text-right">{fmtHoras(l.hn)}</td>
                    <td className="border border-black px-1 py-1 text-right">{fmtHoras(l.he)}</td>
                    <td className="border border-black px-1 py-1 text-right">{fmtHoras(l.unidades)}</td>
                    <td className="border border-black px-1 py-1">{joinSet(l.boletas)}</td>
                    <td className="border border-black px-1 py-1">{joinSet(l.lotes)}</td>
                    <td className="border border-black px-1 py-1">{joinSet(l.supervisores)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-black px-1 py-1 text-right" colSpan={2}>Totales</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtHoras(totales.hn)}</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtHoras(totales.he)}</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtHoras(totales.unidades)}</td>
                  <td className="border border-black px-1 py-1" colSpan={3}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReporteJornadasAprobadasPage;
